/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  loadPaymentServicesSDK,
  PaymentServicesSDK,
} from '@adobe-commerce/payment-services-sdk';
import { Initializer } from '@adobe-commerce/elsie/lib';
import { Lang } from '@adobe-commerce/elsie/i18n';
import { events, Events } from '@adobe-commerce/event-bus';
import * as actions from '@/lib/actions';
import { PaymentLocation, PaymentMethodCode } from '@/api';
import { screamingSnakeToKebabCase } from "@/lib/utils";

export type ConfigProps = {
  /**
   * Adobe Commerce GraphQL API URL, e.g., "https://magento.test/graphql".
   * When not set, this defaults to "/graphql".
   */
  apiUrl?: string;
  /**
   * The Payment Services drop-in may send GraphQL requests on behalf of the shopper. This requires GraphQL
   * authorization, which can be performed using authorization tokens or session cookies.
   *
   * For token-based authorization, the "getCustomerToken" function should return a customer token as a string, or null
   * for guest checkouts. The "getCustomerToken" function should not be provided for session-based authorization.
   *
   * For more information, see: https://developer.adobe.com/commerce/webapi/graphql/usage/authorization-tokens/.
   */
  getCustomerToken?: (() => string | null) | null;
  /**
   * Adobe Commerce store view code on which the Payment Services drop-in should perform GraphQL requests.
   * When not set, no 'Store' header will be sent.
   */
  storeViewCode?: string;
  /**
   * Dictionary of language definitions for the Payment Services drop-in.
   */
  langDefinitions?: Record<'default' | Lang, object>;
};

export const initialize = new Initializer<ConfigProps>({
  /**
   * Triggers asynchronous initialization of the Payment Services drop-in.
   *
   * Note that the returned promise resolves immediately. Thus, promise resolution cannot be used to determine that the
   * initialization finished.
   */
  init: async (config) => {
    const defaultConfig = {};

    initialize.config.setConfig({ ...defaultConfig, ...config });

    loadPaymentServicesSDK()
      .then(async (PaymentServicesSDK) => {
        const [checkoutSDK, productDetailSDK] = await Promise.all([
          initLocation(PaymentServicesSDK, PaymentLocation.CHECKOUT),
          initLocation(PaymentServicesSDK, PaymentLocation.PRODUCT_DETAIL),
        ]);

        actions.initializationSucceeded({
          checkout: checkoutSDK,
          productDetail: productDetailSDK,
        });
      })
      .catch((error) => {
        console.error(
          'Error occurred during payment services drop-in initialization.',
          error
        );
        actions.initializationFailed();
      });
  },

  listeners: () => [
    // events.on('authenticated', (authenticated) => {
    //   console.log('authenticated', authenticated);
    // }),
  ],
});

async function initLocation(
  SDKClass: typeof PaymentServicesSDK,
  location: PaymentLocation
) {
  const config = initialize.config.getConfig();
  const sdk = new SDKClass({
    gqlApiUrl: config?.apiUrl,
    commerceVersion: "2.4.7",
    storeViewCode: config?.storeViewCode,
    getCustomerToken: config?.getCustomerToken,
    isGuestCustomer: () => !events.lastPayload("authenticated"),
  });
  await sdk.Payment.init({ location });
  notifyLocationInitialized(sdk, location);
  return sdk;
}

function notifyLocationInitialized(
  sdk: PaymentServicesSDK,
  location: PaymentLocation
) {
  const name = `payment-services/initialized/${screamingSnakeToKebabCase(
    location
  )}` as keyof Events;

  const payload: { availablePaymentMethods: PaymentMethodCode[] } = {
    availablePaymentMethods: [],
  };

  if (sdk.Payment.ApplePay.isAvailable()) {
    payload.availablePaymentMethods.push(PaymentMethodCode.APPLE_PAY);
  }

  if (sdk.Payment.CreditCard.isAvailable()) {
    payload.availablePaymentMethods.push(PaymentMethodCode.CREDIT_CARD);
  }

  events.emit(name, payload);
}

export const config = initialize.config;
